<template>
  <div class="dotcom-youtube">
    <div class="dotcom-youtube__video-container">
      <div ref="container"></div>
    </div>

    <h2 v-if="titleLabel" class="dotcom-youtube__title">{{ titleLabel }}</h2>
    <div class="dotcom-youtube__text san-richtext" v-html="text"></div>
  </div>
</template>

<script>
import YouTubePlayer from 'youtube-player'

const ONETRUST_CATEGORY = 'C0004'

// YouTue Docs
// https://github.com/gajus/youtube-player
// https://developers.google.com/youtube/iframe_api_reference
// https://developers.google.com/youtube/player_parameters

// Autoplay
// https://developer.chrome.com/blog/autoplay/
// https://stackoverflow.com/a/50272974

export default {
  name: 'DotcomYoutube',
  props: {
    titleLabel: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      default: '',
    },
  },
  async mounted() {
    let { src } = this.$attrs
    let [, videoId] = src.match(/\/embed\/([^?]*)\?/)
    let videoParams = this.extractParams(src)
    let videoOptions = {
      width: '100%',
      height: '100%',
      playerVars: videoParams,
    }

    if (!this.isYoutubeTrackingAllowed()) {
      videoOptions.host = 'https://www.youtube-nocookie.com'
    }

    this.player = YouTubePlayer(this.$refs.container, videoOptions)

    this.player.on('ready', event => {
      this.player.cueVideoById(videoId)
    })

    this.player.on('error', event => {
      this.reportUnavailableVideo(videoId, event.data)
    })
  },
  methods: {
    extractParams(src) {
      let entries = new URLSearchParams(src.split('?')[1])
      let result = {}

      for (let [key, value] of entries) {
        try {
          // Numbers are extracted as strings. So, we parse them here.
          result[key] = JSON.parse(value)
        } catch (error) {
          // If the value wasn't valid JSON we just set it as-is.
          result[key] = value
        }
      }

      return result
    },
    async reportUnavailableVideo(videoId, errorCode) {
      if (errorCode >= 100) {
        // report unavailable video to Instana
        ineum('youtubeUnavailable', videoId)
      }
    },
    isYoutubeTrackingAllowed() {
      if (window.OptanonActiveGroups) {
        let groups = window.OptanonActiveGroups.split(',').filter(Boolean)
        return groups.includes(ONETRUST_CATEGORY)
      } else {
        return false
      }
    },
  },
}
</script>

<style lang="postcss">
.dotcom-youtube {
  position: relative;
  width: auto;
  padding-top: 15px;

  &__video-container {
    position: relative;
    padding-bottom: 56.25%;
    padding-top: 30px;
    margin-bottom: 30px;
    height: 0;

    /**
    * is needed because the clipping of the image is affected by transform
    * animation of other components see:
    * stackoverflow.com/questions/27934887/images-border-radius-doesnt-work-during-css-transition
    */
    z-index: 2;
    overflow: hidden;
    border-radius: var(--border-radius);
    box-shadow: 0 0 30px 0 rgba(0, 0, 0, 0.4);

    iframe,
    object,
    embed {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }

  &__title,
  &__text {
    font-style: normal;
    color: var(--c-primary-neutral-1);
  }

  &__title {
    font-weight: 700;
    font-size: 18px;
    line-height: 1.25;
    margin-bottom: 15px;
  }

  &__text {
    font-weight: 400;
    font-size: 15px;
    line-height: 1.35;
    overflow: hidden;
  }
}
</style>
